// src/pages/OrderViewer/index.tsx
import React, { useEffect, useState } from "react";
import { Step, StepLabel, Stepper } from "@mui/material";
import { auth } from "../../classes/auth";
import { useNavigate, useParams } from "react-router-dom";
import { OrderDetail, OrderSummary } from "./types";
import { searchOrdersByPhone, getOrderDetail } from "../../services/orderViewerService";
import Icon from "../../components/ui/Icon";
import PhoneStep from "./components/PhoneStep";
import OrdersListStep from "./components/OrdersListStep";
import OrderDetailStep from "./components/OrderDetailStep";
import { PageContainer, BackButton, Title, Intro } from "./styles";

const steps = ["Telefone", "Pedidos", "Detalhes"];

const OrderViewer: React.FC = () => {
  const { storeId } = useParams<{ storeId: string | undefined }>();
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);

  const [phone, setPhone] = useState("");
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [, setSelectedOrderId] = useState<string | null>(null);
  const [orderDetail, setOrderDetail] = useState<OrderDetail | null>(null);

  const [loadingStep1, setLoadingStep1] = useState(false);
  const [loadingStep2, setLoadingStep2] = useState(false);
  const [errorStep1, setErrorStep1] = useState<string | null>(null);
  const [errorStep2, setErrorStep2] = useState<string | null>(null);

  const handleSearchOrders = async () => {
    setErrorStep1(null);
    setErrorStep2(null);
    setLoadingStep1(true);

    try {
      const data = await searchOrdersByPhone(storeId, phone);
      setOrders(data);
      setActiveStep(1);
    } catch (err: any) {
      setErrorStep1(err.message || "Erro inesperado ao buscar pedidos.");
    } finally {
      setLoadingStep1(false);
    }
  };

  const handleSelectOrder = async (order: OrderSummary) => {
    setSelectedOrderId(order.id);
    setOrderDetail(null);
    setErrorStep2(null);
    setLoadingStep2(true);

    try {
      const data = await getOrderDetail(order.id);
      setOrderDetail(data);
      setActiveStep(2);
    } catch (err: any) {
      setErrorStep2(err.message || "Erro inesperado ao buscar detalhes.");
      // Also advance to step 2 on failure (not just success) -- OrderDetailStep is
      // the only place errorStep2 ever renders, and it only mounts at activeStep 2.
      // Without this, a failed fetch was silently swallowed: the error was set but
      // never shown anywhere, since the page stayed on the orders list. Do not
      // remove this thinking it's a redundant duplicate of the try block's call.
      setActiveStep(2);
    } finally {
      setLoadingStep2(false);
    }
  };

  const handleBackToPhone = () => {
    setActiveStep(0);
    setOrders([]);
    setSelectedOrderId(null);
    setOrderDetail(null);
  };

  const handleBackToList = () => {
    setActiveStep(1);
  };

  useEffect(() => {
    auth();
  }, []);

  return (
    <PageContainer>
      <BackButton type="button" onClick={() => navigate(-1)}>
        <Icon name="back" size={17} strokeWidth={2.4} />
        Voltar à loja
      </BackButton>
      <Title>Visualizar compra</Title>
      <Intro>Consulte seus pedidos informando o telefone utilizado na compra.</Intro>

      <Stepper activeStep={activeStep} sx={{ mt: 2, mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <PhoneStep
          phone={phone}
          setPhone={setPhone}
          loading={loadingStep1}
          error={errorStep1}
          onSubmit={handleSearchOrders}
        />
      )}

      {activeStep === 1 && (
        <OrdersListStep
          phone={phone}
          orders={orders}
          loading={loadingStep1}
          error={errorStep1}
          onSelectOrder={handleSelectOrder}
          onBack={handleBackToPhone}
        />
      )}

      {activeStep === 2 && (
        <OrderDetailStep
          order={orderDetail}
          loading={loadingStep2}
          error={errorStep2}
          onBackToList={handleBackToList}
        />
      )}
    </PageContainer>
  );
};

export default OrderViewer;
